import moment from 'moment';
import {
  ISessionCertificateItem,
  ISessionUpdate,
} from 'pages/dashboard/components/ent-account/components/edit-account/components/experts/components/edit-expert/components/sessions/components/view-session/interface';
import { IDisbursement } from 'pages/dashboard/components/ent-account/components/edit-account/interface';
import { dateTimeFormat } from 'utils/formats';
import { buildBookedByUserIdLookup } from 'utils/session-participants';

interface DownloadSessionSlotCsvOptions {
  purchases: ISessionUpdate[];
  sessionTitle?: string;
  location?: string;
  slot?: {
    from?: string;
    to?: string;
  };
}

interface DownloadSessionCertificatesCsvOptions {
  certificates: ISessionCertificateItem[];
  sessionTitle?: string;
}

const sanitizeFileSegment = (value: string) => value.replaceAll(/[^a-z0-9]+/gi, '');
const getFullName = (firstName: string, lastName: string) => `${firstName} ${lastName}`.trim();
const getCertificateLifecycleStatusLabel = (status?: string) => (
  status ? `${status.charAt(0)}${status.slice(1).toLowerCase()}` : '-'
);

export const downloadSessionSlotCsv = ({
  purchases,
  sessionTitle,
  location,
  slot,
}: DownloadSessionSlotCsvOptions) => {
  if (!purchases || purchases.length === 0) {
    return;
  }

  const startTime = slot?.from ? moment(slot.from) : null;
  const endTime = slot?.to ? moment(slot.to) : null;
  const locationLabel = location ?? '-';

  const headers = [
    'name',
    'email',
    'location',
    'start_time',
    'end_time',
    'date_booked',
    'booked_by',
    'session_title',
  ];
  const bookedByUserId = buildBookedByUserIdLookup(purchases);

  const csvRows = purchases.map((purchase) => [
    purchase.user?.name ?? '-',
    purchase.user?.email ?? '-',
    locationLabel,
    startTime ? startTime.format('MMM DD, YYYY hh:mm A') : '-',
    endTime ? endTime.format('MMM DD, YYYY hh:mm A') : '-',
    purchase?.date ? dateTimeFormat(purchase.date) : '-',
    purchase.isLocationParticipant
      ? bookedByUserId.get(purchase.user?.id ?? '') ?? '-'
      : '-',
    sessionTitle ?? '-',
  ]);

  const csvContent = [headers, ...csvRows]
    .map((row) => row.map((value) => `"${String(value).replaceAll(/"/g, '""')}"`).join(','))
    .join('\n');

  const formattedTitle = sanitizeFileSegment(sessionTitle ?? 'Session');
  const formattedDate = startTime ? startTime.format('DDMMMYYYY') : 'UnknownDate';
  const formattedTime = startTime ? startTime.format('hhmmA') : 'UnknownTime';
  const fileName = `${formattedTitle}_${formattedDate}_${formattedTime}.csv`;

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const downloadSessionCertificatesCsv = ({
  certificates,
  sessionTitle,
}: DownloadSessionCertificatesCsvOptions) => {
  const headers = [
    'user_name',
    'email',
    'completion_percentage',
    'issuance_status',
    'issued_on',
    'certificate_status',
  ];

  const csvRows = certificates.map((certificateItem) => [
    getFullName(
      certificateItem.userSession.user.firstName,
      certificateItem.userSession.user.lastName,
    ) || '-',
    certificateItem.userSession.user.email ?? '-',
    `${certificateItem.completion}%`,
    certificateItem.certificate ? 'Issued' : 'Not Issued',
    certificateItem.certificate?.issuedOn
      ? dateTimeFormat(certificateItem.certificate.issuedOn)
      : '-',
    getCertificateLifecycleStatusLabel(certificateItem.certificate?.status),
  ]);

  const csvContent = [headers, ...csvRows]
    .map((row) => row.map((value) => `"${String(value).replaceAll(/"/g, '""')}"`).join(','))
    .join('\n');

  const formattedTitle = sanitizeFileSegment(sessionTitle ?? 'SessionCertificates')
    || 'SessionCertificates';
  const fileName = `${formattedTitle}_Certificates_${moment().format('DDMMMYYYY_hhmmA')}.csv`;

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const downloadDisbursementsCsv = (disbursements: IDisbursement[]) => {
  if (!disbursements || disbursements.length === 0) {
    return;
  }

  const headers = [
    'id',
    'created_at',
    'entity_type',
    'entity_id',
    'product_type',
    'product_id',
    'transaction_id',
    'entity_percentage',
    'entity_amount_usd',
    'entity_amount_aud',
    'entity_amount_gbp',
    'gross_amount_usd',
    'gross_amount_aud',
    'gross_amount_gbp',
    'stripe_fee_usd',
    'stripe_fee_aud',
    'stripe_fee_gbp',
    'total_amount_usd',
    'total_amount_aud',
    'total_amount_gbp',
    'status',
    'is_disbursable',
    'retry_count',
    'last_retry_reason',
    'stripe_transfer_id',
    'stripe_account_id',
  ];

  const csvRows = disbursements.map((item) => [
    item._id ?? '-',
    item.createdDate ? moment(item.createdDate).format('MMM DD, YYYY hh:mm A') : '-',
    item.entityType ?? '-',
    item.entityId ?? '-',
    item.productType ?? '-',
    item.product ?? '-',
    item.transaction ?? '-',
    item.entityPercentage ?? 0,
    item.entityAmount?.USD ?? '-',
    item.entityAmount?.AUD ?? '-',
    item.entityAmount?.GBP ?? '-',
    item.grossAmount?.USD ?? '-',
    item.grossAmount?.AUD ?? '-',
    item.grossAmount?.GBP ?? '-',
    item.stripeFeeAmount?.USD ?? '-',
    item.stripeFeeAmount?.AUD ?? '-',
    item.stripeFeeAmount?.GBP ?? '-',
    item.totalAmount?.USD ?? '-',
    item.totalAmount?.AUD ?? '-',
    item.totalAmount?.GBP ?? '-',
    item.status ?? '-',
    item.isDisbursable ? 'yes' : 'no',
    item.retryCount ?? 0,
    item.retryReasons?.length
      ? item.retryReasons[item.retryReasons.length - 1].reason
      : '-',
    item.stripeTransferObject?.id ?? '-',
    item.meta?.entStripeAccountId ?? item.meta?.expertStripeAccountId ?? '-',
  ]);

  const csvContent = [headers, ...csvRows]
    .map((row) => row.map((value) => `"${String(value).replaceAll(/"/g, '""')}"`).join(','))
    .join('\n');

  const fileName = `Disbursements_${moment().format('DDMMMYYYY_hhmmA')}.csv`;

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export default downloadSessionSlotCsv;
